define([
  "marionette",
  "underscore",
  "messaging/messenger",
  "./notificationsView",
  "modules/admin/userAdmin/notifications/notificationModel",
  "modules/shared/treeList/treeListModule",
  "modules/shared/treeList/treeListModel",
  "i18n/i18n",
], function (
  Marionette,
  _,
  Messenger,
  NotificationsView,
  NotificationModel,
  TreeListModule,
  TreeListModel,
  i18next,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "Notifications";
      this.region = options.region;
      this.user = options.user;
      this.namespace = options.namespace;
      Messenger.vent.on(this.name + ":treeList:node:checked", this._onNodeCheckedChanged, this);
    },
    show: function () {
      this.collection = this.user.notifications();
      this.view = new NotificationsView.view();
      this.view.on("show", this._onShow, this);
      this.showRegion(this.view);
    },
    _onShow: function () {
      this.collection.fetch();
      this.collection.onSynced(() => {
        this._prepareAndShowTree();
      });
    },
    _prepareAndShowTree: function () {
      if (this.collection.isEmpty()) {
        const modelsToAdd = this._findDataNodes(this._rawTreeData()).map((notification) => {
          return new NotificationModel.model({
            userId: this.user.get("userId"),
            notificationIdentifier: notification,
            preventEmail: false,
          });
        });
        this.collection.add(modelsToAdd);
      }

      // Build tree with checked flags based on collection
      this.treeData = this._buildModelWithChecks();
      this.root = new TreeListModel.collection(this.treeData);

      // Show TreeListModule with correct model
      this.notificationTreeList = this.addSubModule(
        new TreeListModule({
          region: this.view.notificationsList,
          namespace: this.name,
        }),
      );

      this.notificationTreeList.show({ model: this.root });
    },
    _onNodeCheckedChanged: function (nodeId, isChecked) {
      let notification = this.collection.findWhere({
        notificationIdentifier: nodeId,
      });
      if (notification) {
        notification.set("preventEmail", !isChecked);
      } else {
        notification = new NotificationModel.model({
          userId: this.user.get("userId"),
          notificationIdentifier: nodeId,
          preventEmail: !isChecked,
        });
        this.collection.add(notification);
      }
    },
    _buildModelWithChecks: function () {
      const applyChecks = (nodes) => {
        return nodes.map((node) => {
          const isLeaf = this._isLeaf(node);

          let isChecked = false;
          if (isLeaf && node.data) {
            const matching = this.collection.findWhere({
              notificationIdentifier: node.data,
            });
            // Checked by default unless explicitly disabled in DB
            isChecked = !matching || !matching.get("preventEmail");
          }

          const newNode = {
            ...node,
            checked: isLeaf ? isChecked : false, // Only apply checked to leaf nodes
          };

          if (node.nodes) {
            newNode.nodes = applyChecks(node.nodes);
          }

          return newNode;
        });
      };
      return applyChecks(this._rawTreeData());
    },
    _findDataNodes: function (treeData) {
      const notifications = [];
      const traverse = (node) => {
        const isLeaf = this._isLeaf(node);
        if (isLeaf && node.data) {
          notifications.push(node.data);
        }
        if (node.nodes) {
          node.nodes.forEach(traverse);
        }
      };
      treeData.forEach(traverse);
      return notifications;
    },
    _isLeaf: function (node) {
      return !node.nodes || node.nodes.length === 0;
    },
    _rawTreeData: function () {
      return [
        {
          name: i18next.t("user.notificationsList.tasks"),
          data: "TASK_LABEL",
          nodes: [
            {
              name: i18next.t("user.notificationsList.taskStatus"),
              data: "TASK_STATUS_LABEL",
              nodes: [
                {
                  name: i18next.t("user.notificationsList.creatorTaskInProgress"),
                  data: "CREATOR_TASK_IN_PROGRESS",
                },
                {
                  name: i18next.t("user.notificationsList.creatorTaskCompleted"),
                  data: "CREATOR_TASK_COMPLETED",
                },
                {
                  name: i18next.t("user.notificationsList.creatorTaskRejected"),
                  data: "CREATOR_TASK_REJECTED",
                },
                {
                  name: i18next.t("user.notificationsList.assigneeTaskInProgress"),
                  data: "ASSIGNEE_TASK_IN_PROGRESS",
                },
                {
                  name: i18next.t("user.notificationsList.assigneeTaskCompleted"),
                  data: "ASSIGNEE_TASK_COMPLETED",
                },
              ],
            },
            {
              name: i18next.t("user.notificationsList.taskDeleted"),
              data: "TASK_DELETED",
              nodes: [
                {
                  name: i18next.t("user.notificationsList.assigneeTaskDeleted"),
                  data: "ASSIGNEE_TASK_DELETED",
                },
              ],
            },
            {
              name: i18next.t("user.notificationsList.taskDueDateReminderLabel"),
              data: "TASK_DUE_DATE_REMINDER_LABEL",
              nodes: [
                {
                  name: i18next.t("user.notificationsList.dueDateInOneWeek"),
                  data: "DUE_DATE_IN_ONE_WEEK",
                },
                {
                  name: i18next.t("user.notificationsList.dueDateTomorrow"),
                  data: "DUE_DATE_TOMORROW",
                },
                {
                  name: i18next.t("user.notificationsList.dueDatePassed"),
                  data: "DUE_DATE_PASSED",
                },
              ],
            },
            {
              name: i18next.t("user.notificationsList.taskAssignedLabel"),
              data: "TASK_ASSIGNED_LABEL",
              nodes: [
                {
                  name: i18next.t("user.notificationsList.taskAssignedYou"),
                  data: "TASK_ASSIGNED_YOU",
                },
                {
                  name: i18next.t("user.notificationsList.taskAssignedDepartment"),
                  data: "TASK_ASSIGNED_DEPARTMENT",
                },
              ],
            },
            {
              name: i18next.t("user.notificationsList.taskDueDateChangedLabel"),
              data: "TASK_DUE_DATE_CHANGED_LABEL",
              nodes: [
                {
                  name: i18next.t("user.notificationsList.creatorDueDateChanged"),
                  data: "CREATOR_DUE_DATE_CHANGED",
                },
                {
                  name: i18next.t("user.notificationsList.assigneeDueDateChanged"),
                  data: "ASSIGNEE_DUE_DATE_CHANGED",
                },
              ],
            },
            {
              name: i18next.t("user.notificationsList.taskStepStatusLabel"),
              data: "TASK_STEP_STATUS_LABEL",
              nodes: [
                {
                  name: i18next.t("user.notificationsList.creatorStepInTaskCompleted"),
                  data: "CREATOR_STEP_IN_TASK_COMPLETED",
                },
                {
                  name: i18next.t("user.notificationsList.creatorStepInLocaleInTaskCompleted"),
                  data: "CREATOR_STEP_IN_LOCALE_IN_TASK_COMPLETED",
                },
                {
                  name: i18next.t("user.notificationsList.creatorAllStepsInLocaleInTaskCompleted"),
                  data: "CREATOR_ALL_STEPS_IN_LOCALE_IN_TASK_COMPLETED",
                },
                {
                  name: i18next.t("user.notificationsList.creatorStepInTaskInProgress"),
                  data: "CREATOR_STEP_IN_TASK_IN_PROGRESS",
                },
                {
                  name: i18next.t("user.notificationsList.creatorStepInTaskRejected"),
                  data: "CREATOR_STEP_IN_TASK_REJECTED",
                },
                {
                  name: i18next.t("user.notificationsList.assigneeStepInTaskInProgress"),
                  data: "ASSIGNEE_STEP_IN_TASK_IN_PROGRESS",
                },
                {
                  name: i18next.t("user.notificationsList.assigneeStepInTaskCompleted"),
                  data: "ASSIGNEE_STEP_IN_TASK_COMPLETED",
                },
                {
                  name: i18next.t("user.notificationsList.assigneeStepInLocaleInTaskCompleted"),
                  data: "ASSIGNEE_STEP_IN_LOCALE_IN_TASK_COMPLETED",
                },
              ],
            },
            {
              name: i18next.t("user.notificationsList.taskChanged"),
              data: "TASK_CHANGED",
              nodes: [
                {
                  name: i18next.t("user.notificationsList.assigneeTaskChanged"),
                  data: "ASSIGNEE_TASK_CHANGED",
                },
              ],
            },
          ],
        },
        {
          name: i18next.t("user.notificationsList.topics"),
          data: "TOPICS",
          nodes: [
            {
              name: i18next.t("user.notificationsList.topicSearchRefresh"),
              data: "TOPIC_SEARCH_REFRESH",
              nodes: [
                {
                  name: i18next.t("user.notificationsList.completedSuccessfully"),
                  data: "COMPLETED_SUCCESSFULLY",
                },
                {
                  name: i18next.t("user.notificationsList.completedWithErrors"),
                  data: "COMPLETED_WITH_ERRORS",
                },
                {
                  name: i18next.t("user.notificationsList.refreshFailed"),
                  data: "REFRESH_FAILED",
                },
              ],
            },
            {
              name: i18next.t("user.notificationsList.topicEdit"),
              data: "TOPIC_EDIT",
              nodes: [
                {
                  name: i18next.t("user.notificationsList.topicEditsRequestedByEndUser"),
                  data: "TOPIC_EDITS_REQUESTED_BY_END_USER",
                },
              ],
            },
          ],
        },
      ];
    },
  });
});
